import { Accessor, createSignal, Show } from 'solid-js';
import { Checkbox } from '../../ui-components/inputs/checkbox/checkbox';
import { Link } from '../../ui-components/link/link';
import { PrivacyPolicyModal } from '../privacy-policy-modal/privacy-policy-modal';
import { Errors, FormValue, Localize } from './forms-types';
import { FieldErrorMessage } from './forms-fields/primitives';
import { createCountryTerms } from './country-terms';

type Terms = ReturnType<typeof createCountryTerms>;

type PolicyConsentProps = {
    formValues: Accessor<FormValue>;
    errors: Accessor<Errors>;
    onCheck: (slug: string) => void;
    localize: Localize;
    privacyPolicyPage: string;
    terms: Terms;
};

export const PolicyConsent = (props: PolicyConsentProps) => {
    // props.terms is a plain bundle of stable accessor functions — destructuring
    // it once (vs reading props.terms.x everywhere) is fine and idiomatic.
    const {
        getSelectedCountryCode,
        getUserTypeForTerms,
        isDoubleConsent,
        isTermsConfigured,
        hasMarketingContent,
        privacyCheckboxText,
        privacyLinkText,
        marketingCheckboxText,
        marketingLinkText,
    } = props.terms;

    const [showPrivacyModal, setShowPrivacyModal] = createSignal(false);
    const [showMarketingModal, setShowMarketingModal] = createSignal(false);

    const linkButtonClass = 'text-cvi-fg-link font-cvi-medium hover:underline cursor-pointer';
    const captionClass = 'py-cvi-4 label-md font-cvi-regular text-cvi-fg-default';

    // Legacy fallback when the Terms & Conditions ACF has no privacy policy content:
    // a single checkbox + external link to the privacy policy page.
    const renderLegacy = () => (
        <>
            <div class="flex flex-row items-center gap-cvi-6">
                <Checkbox
                    isChecked={props.formValues()?.acceptPolicy}
                    whenClicked={() => props.onCheck('acceptPolicy')}
                    value="privacy-policy-consent"
                    name={props.localize('agree-to-terms', 'I agree to the terms and conditions')}
                    aria-label={props.localize('agree-to-terms', 'I agree to the terms and conditions')}
                    aria-describedby="desc-acceptPolicy"
                />

                <span id="desc-acceptPolicy" style={{ display: 'none' }}>
                    {props.localize('agree-to-terms', 'I agree to the terms and conditions')}
                </span>

                <Link noBlockSpacing={true} label={props.localize('read-more-here', 'Read more here')} url={props.privacyPolicyPage || '/'} />
            </div>

            <Show when={props.errors()['acceptPolicy']}>
                <FieldErrorMessage errors={props.errors} fieldKey="acceptPolicy" />
            </Show>
        </>
    );

    const singleConsentAriaLabel = () => hasMarketingContent()
        ? `${privacyCheckboxText()} ${privacyLinkText()} ${props.localize('and', 'and')} ${marketingLinkText()}`
        : `${privacyCheckboxText()} ${privacyLinkText()}`;

    // Single consent: one checkbox covers privacy + (optionally) marketing, with both links inline.
    const renderSingleConsent = () => (
        <>
            <div class="flex flex-row items-start gap-cvi-4">
                <div class="flex-shrink-0">
                    <Checkbox
                        isChecked={props.formValues()?.acceptPolicy}
                        whenClicked={() => props.onCheck('acceptPolicy')}
                        value="privacy-policy-consent"
                        name=""
                        aria-label={singleConsentAriaLabel()}
                        aria-describedby="desc-acceptPolicy"
                    />
                </div>
                <span class={captionClass}>
                    {privacyCheckboxText()}&nbsp;<button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        class={linkButtonClass}
                    >{privacyLinkText()}</button>
                    <Show when={hasMarketingContent()}>
                        &nbsp;{props.localize('and', 'and')}&nbsp;<button
                            type="button"
                            onClick={() => setShowMarketingModal(true)}
                            class={linkButtonClass}
                        >{marketingLinkText()}</button>
                    </Show>
                </span>
            </div>

            <span id="desc-acceptPolicy" style={{ display: 'none' }}>
                {singleConsentAriaLabel()}
            </span>

            <Show when={props.errors()['acceptPolicy']}>
                <FieldErrorMessage errors={props.errors} fieldKey="acceptPolicy" />
            </Show>
        </>
    );

    // Double consent: separate checkboxes for privacy and marketing (countries that require it).
    const renderDoubleConsent = () => (
        <>
            <div class="flex flex-row items-start gap-cvi-4">
                <div class="flex-shrink-0">
                    <Checkbox
                        isChecked={props.formValues()?.acceptPolicy}
                        whenClicked={() => props.onCheck('acceptPolicy')}
                        value="privacy-policy-consent"
                        name=""
                        aria-label={`${privacyCheckboxText()} ${privacyLinkText()}`}
                        aria-describedby="desc-acceptPolicy-double"
                    />
                </div>
                <span class={captionClass}>
                    {privacyCheckboxText()}&nbsp;<button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        class={linkButtonClass}
                    >{privacyLinkText()}</button>
                </span>
            </div>

            <span id="desc-acceptPolicy-double" style={{ display: 'none' }}>
                {`${privacyCheckboxText()} ${privacyLinkText()}`}
            </span>

            <Show when={props.errors()['acceptPolicy']}>
                <FieldErrorMessage errors={props.errors} fieldKey="acceptPolicy" />
            </Show>

            <div class="flex flex-row items-start gap-cvi-4 mt-cvi-6">
                <div class="flex-shrink-0">
                    <Checkbox
                        isChecked={props.formValues()?.acceptMarketingConsent}
                        whenClicked={() => props.onCheck('acceptMarketingConsent')}
                        value="marketing-consent"
                        name=""
                        aria-label={`${marketingCheckboxText()} ${marketingLinkText()}`}
                        aria-describedby="desc-acceptMarketingConsent"
                    />
                </div>
                <span class={captionClass}>
                    {marketingCheckboxText()}&nbsp;<button
                        type="button"
                        onClick={() => setShowMarketingModal(true)}
                        class={linkButtonClass}
                    >{marketingLinkText()}</button>
                </span>
            </div>

            <span id="desc-acceptMarketingConsent" style={{ display: 'none' }}>
                {`${marketingCheckboxText()} ${marketingLinkText()}`}
            </span>

            <Show when={props.errors()['acceptMarketingConsent']}>
                <FieldErrorMessage errors={props.errors} fieldKey="acceptMarketingConsent" />
            </Show>
        </>
    );

    return (
        <Show when={isTermsConfigured()} fallback={renderLegacy()}>
            <Show when={isDoubleConsent()} fallback={renderSingleConsent()}>
                {renderDoubleConsent()}
            </Show>

            <PrivacyPolicyModal
                visible={showPrivacyModal()}
                onClose={() => setShowPrivacyModal(false)}
                userCountryCode={getSelectedCountryCode()}
                userType={getUserTypeForTerms()}
                title={privacyLinkText()}
                contentType="privacy"
            />

            <PrivacyPolicyModal
                visible={showMarketingModal()}
                onClose={() => setShowMarketingModal(false)}
                userCountryCode={getSelectedCountryCode()}
                userType={getUserTypeForTerms()}
                title={marketingLinkText()}
                contentType="marketing"
            />
        </Show>
    );
};
